import { useCallback, useRef, useState } from 'react';

/**
 * @function useFocus
 * @desc this hook provides a focused state variable
 * @desc that keeps a ref focused when the state changes (optional when blurring)
 * @desc useful for buttons or inputs
 * @param  initFocused - whether the state is initially focused
 * @param keepFocusOnBlur - whether to keep the ref focused after blur
 * @returns - an array that contains isFocused, focus, blur and the ref
 */
export default (
  initFocused: boolean,
  keepFocusOnBlur = false
): [boolean, () => void, () => void, React.RefObject<HTMLInputElement>] => {
  // State and setter for focus
  const [isFocused, setFocus] = useState<boolean>(initFocused);

  // ref that will be focused
  const ref = useRef<HTMLInputElement | null>(null);

  // handle initFocus state to true by focusing after a short inital delay
  if (initFocused) {
    setTimeout(() => {
      if (ref.current) {
        ref.current.focus();
      }
    });
  }

  // callbacks
  const focus: () => void = useCallback(() => {
    setFocus(true);
    if (ref.current) {
      ref.current.focus();
    }
  }, []);

  const blur: () => void = useCallback(() => {
    setFocus(false);
    if (ref.current && keepFocusOnBlur) {
      ref.current.focus();
    }
  }, []);

  return [isFocused, focus, blur, ref];
};
